import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, Chip, Button, IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockIcon from '@mui/icons-material/Lock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchoolIcon from '@mui/icons-material/School';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import HeaderPills from './HeaderPills';
import PageShell, { PageTitle } from './PageShell';
import useSplitLayout from '../hooks/useSplitLayout';
import ProArtistCard from './ProArtistCard';
import BackingTrackCard from './BackingTrackCard';
import LessonCard from './LessonCard';
import PackCard from './PackCard';
import PackPurchaseDialog from './PackPurchaseDialog';
import VideoDialog, { openVideo } from './VideoDialog';
import { getModeInfo } from '../utils/musicTheory/index';
import { getPentatonicParentModes, getDisplayRootNote, getModeDisplayLabel } from '../utils/musicTheory';
import formatAccidentals from '../utils/formatAccidentals';
import { getFeaturedArtistForScale, getAllBackingTracksSorted, getAllVideos, getAllBackingTracks, getAllPacks, getPackById, getTracksForPack, getAllLessons, getLessonsForScale, isTrackUnlocked, isPackPromoActive, isContentVisible, FEATURED_ARTISTS } from '../data/artistContent';
import { CONTENT_FLAGS } from '../config/contentFlags';
import { ACCENT_COLOR } from '../config/uiTheme';
import { isInternalBuild } from '../config/distribution';
import usePackPurchases from '../hooks/usePackPurchases';
import { useSignedIn } from '../hooks/useSignedIn';
import { useEntitlement } from '../hooks/useEntitlement';
import { isPackUnlocked } from '../data/entitlements';

// Video Card Component (free content)
const VideoCard = ({ badge, badgeColor, title, subtitle, duration, onClick }) => (
  <Box onClick={onClick} sx={{
    cursor: onClick ? 'pointer' : 'default',
    backgroundColor: 'rgba(30, 30, 40, 0.6)',
    borderRadius: '16px',
    overflow: 'hidden',
    mb: 2
  }}>
    {/* Thumbnail placeholder */}
    <Box sx={{
      position: 'relative',
      width: '100%',
      paddingTop: '56.25%',
      backgroundColor: 'rgba(60, 60, 80, 0.4)'
    }}>
      {/* Play button */}
      <Box onClick={onClick} sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 64,
        height: 64,
        borderRadius: '50%',
        backgroundColor: '#f97316',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 1,
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'translate(-50%, -50%) scale(1.1)'
        }
      }}>
        <PlayArrowIcon sx={{ fontSize: 32, color: 'white', ml: 0.5 }} />
      </Box>
      {/* Duration badge */}
      {duration && (
        <Box sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          backgroundColor: 'rgba(0,0,0,0.7)',
          px: 1,
          py: 0.25,
          borderRadius: '4px'
        }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'white' }}>{duration}</Typography>
        </Box>
      )}
    </Box>
    {/* Info section */}
    <Box sx={{ p: 2 }}>
      {badge && (
        <Box sx={{
          display: 'inline-block',
          backgroundColor: badgeColor || '#f97316',
          px: 1,
          py: 0.25,
          borderRadius: '4px',
          mb: 1
        }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'white', textTransform: 'uppercase' }}>
            {badge}
          </Typography>
        </Box>
      )}
      <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: 'white', mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

// Mini Video Card for horizontal scroll
const MiniVideoCard = ({ title, duration, channel, thumbnailUrl, onClick }) => (
  <Box onClick={onClick} sx={{
    cursor: onClick ? 'pointer' : 'default',
    minWidth: 180,
    maxWidth: 180,
    backgroundColor: 'rgba(30, 30, 40, 0.6)',
    borderRadius: '12px',
    overflow: 'hidden',
    flexShrink: 0
  }}>
    {/* Thumbnail */}
    <Box sx={{
      position: 'relative',
      width: '100%',
      paddingTop: '56.25%',
      backgroundColor: 'rgba(60, 60, 80, 0.4)',
      backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      <Box onClick={onClick} sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: 'rgba(249, 115, 22, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1
      }}>
        <PlayArrowIcon sx={{ fontSize: 24, color: 'white', ml: 0.3 }} />
      </Box>
      {duration && (
        <Box sx={{
          position: 'absolute',
          bottom: 4,
          right: 4,
          backgroundColor: 'rgba(0,0,0,0.7)',
          px: 0.75,
          py: 0.1,
          borderRadius: '3px'
        }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'white' }}>{duration}</Typography>
        </Box>
      )}
    </Box>
    <Box sx={{ p: 1.5 }}>
      <Typography sx={{
        fontSize: '0.85rem',
        fontWeight: 'bold',
        color: 'white',
        mb: 0.25,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical'
      }}>
        {title}
      </Typography>
      {channel && (
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
          {channel}
        </Typography>
      )}
    </Box>
  </Box>
);

// Quick Lick Card
const QuickLickCard = ({ name, description, pattern }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(30, 30, 40, 0.6)',
    borderRadius: '12px',
    p: 1.5,
    mb: 1.5,
    gap: 2
  }}>
    {/* Play button */}
    <Box sx={{
      width: 48,
      height: 48,
      borderRadius: '50%',
      backgroundColor: 'rgba(249, 115, 22, 0.15)',
      border: '2px solid rgba(249, 115, 22, 0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(249, 115, 22, 0.25)',
        borderColor: 'rgba(249, 115, 22, 0.6)'
      }
    }}>
      <PlayArrowIcon sx={{ fontSize: 24, color: '#f97316', ml: 0.3 }} />
    </Box>
    {/* Info */}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 'bold', color: 'white' }}>
        {name}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
        {description}
      </Typography>
    </Box>
    {/* Pattern badge */}
    <Box sx={{
      backgroundColor: 'rgba(255,255,255,0.1)',
      px: 1.5,
      py: 0.5,
      borderRadius: '8px',
      flexShrink: 0
    }}>
      <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)', fontFamily: 'monospace' }}>
        {pattern}
      </Typography>
    </Box>
  </Box>
);

// Section Header
const SectionHeader = ({ title, action, badge }) => (
  <Box className="jam-section-header" sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 2,
    mt: 3
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'white' }}>{title}</Typography>
      {badge && (
        <Box sx={{
          backgroundColor: '#fbbf24',
          px: 1,
          py: 0.1,
          borderRadius: '6px',
          ml: 0.5
        }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 'bold', color: '#000' }}>
            {badge}
          </Typography>
        </Box>
      )}
    </Box>
    {action && (
      <Typography sx={{
        fontSize: '0.9rem',
        color: '#f97316',
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' }
      }}>
        {action}
      </Typography>
    )}
  </Box>
);

const ContentRail = ({ id, title, count, onViewAll, children }) => {
  const railRef = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: true });
  const measure = () => {
    const rail = railRef.current;
    if (!rail) return;
    setEdges({ start: rail.scrollLeft <= 1, end: rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1 });
  };

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(railRef.current);
    return () => observer.disconnect();
  }, [count]);

  const scroll = (direction) => {
    const rail = railRef.current;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    rail.scrollBy({ left: direction * rail.clientWidth * 0.85, behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  return (
    <Box component="section" aria-label={title} sx={{ minWidth: 0, mt: 3, order: id === 'jam-lessons' ? 3 : 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Typography component="h2" sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'white' }}>{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {onViewAll && (
            <Button data-jam-focus={`view-${id}`} onClick={onViewAll} aria-label={`View all ${title} (${count})`}
              sx={{ color: ACCENT_COLOR, textTransform: 'none', whiteSpace: 'nowrap', minHeight: 44 }}>
              View all ({count})
            </Button>
          )}
          <Box sx={{ display: 'none', '@media (hover: hover) and (pointer: fine)': { display: 'flex' } }}>
            <IconButton aria-label={`Previous ${title}`} aria-controls={id} disabled={edges.start} onClick={() => scroll(-1)}
              sx={{ color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}><ChevronLeftIcon /></IconButton>
            <IconButton aria-label={`Next ${title}`} aria-controls={id} disabled={edges.end} onClick={() => scroll(1)}
              sx={{ color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}><ChevronRightIcon /></IconButton>
          </Box>
        </Box>
      </Box>
      <Box id={id} data-jam-rail={id} ref={railRef} onScroll={measure} tabIndex={0} aria-label={`${title} cards`}
        sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, scrollSnapType: 'x proximity',
          '& > *': { scrollSnapAlign: 'start' }, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        {children}
      </Box>
    </Box>
  );
};

const catalogGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
  gap: 2,
  alignItems: 'start',
  '& > *': { minWidth: 0, maxWidth: 'none', width: '100%' }
};

/**
 * Compact row for non-matching backing tracks (Explore section).
 * Lightweight discovery item — tap jams into the track's native scale.
 */
const BackingTrackRow = ({ track, artist, onJam, unlocked = true, onUnlock }) => {
  const accentColor = ACCENT_COLOR;
  // Orthographe enharmonique correcte (Bb Lydian, pas A# Lydian) ; un morceau
  // multi-tonal l'annonce au lieu d'afficher une seule gamme trompeuse.
  const extraKeys = (track.keyCenters || []).length - 1;
  const scaleLabel = `${getDisplayRootNote(track.rootNote, track.mode)} ${track.mode}${extraKeys > 0 ? ` +${extraKeys} keys` : ''}`;
  return (
    <Box
      onClick={unlocked ? onJam : onUnlock}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
        mb: 1,
        borderRadius: '12px',
        cursor: 'pointer',
        backgroundColor: 'rgba(30,30,40,0.5)',
        transition: 'all 0.15s ease',
        '&:hover': { backgroundColor: 'rgba(40,40,55,0.7)' }
      }}
    >
      <Box sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        flexShrink: 0,
        background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}99 100%)`,
        ...(artist?.avatar && {
          backgroundImage: `url(${artist.avatar})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {!artist?.avatar && (
          <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: 'white' }}>
            {artist ? artist.name.split(' ').map(n => n[0]).join('') : '♪'}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'white' }}>
          {track.title}
        </Typography>
        {artist && (
          <Typography noWrap sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
            {artist.name}
          </Typography>
        )}
      </Box>
      {/* Pill verte = jouable maintenant ; grise + cadenas = a debloquer */}
      <Box sx={{
        px: 1,
        py: 0.25,
        borderRadius: '6px',
        flexShrink: 0,
        backgroundColor: unlocked ? 'rgba(16,185,129,0.18)' : 'rgba(255,255,255,0.1)'
      }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: unlocked ? '#10b981' : 'rgba(255,255,255,0.7)' }}>
          {scaleLabel}
        </Typography>
      </Box>
      {!unlocked && <LockIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', flexShrink: 0 }} />}
    </Box>
  );
};

/**
 * Learn/Inspire component - displays inspirational content based on current scale
 */
const RELEVANCE_LABELS = {
  perfect: 'Perfect Match',
  compatible: 'Compatible',
  sameScale: 'Other Key',
  other: 'Explore'
};
const RELEVANCE_COLORS = {
  perfect: '#10b981',
  compatible: '#3b82f6',
  sameScale: '#0ea5e9',
  other: 'rgba(255,255,255,0.3)'
};

const Learn = forwardRef(function Learn({ rootNote, currentMode, currentProgression, currentMood, onScalePillClick, onScaleChange, onEnterJamMode, onEnterLesson, onStopOtherAudio, onRequestSignUp, openPackId = null, onOpenPackIdConsumed, desktopBrowser = false, active = true }, ref) {
  const modeInfo = getModeInfo(currentMode);
  const modeLabel = getModeDisplayLabel(currentMode);
  const scaleLabel = `${formatAccidentals(getDisplayRootNote(rootNote, currentMode))} ${modeLabel}`;
  // Pentatonics: the parent scales they fit inside (same root). Empty for
  // non-pentatonic modes, so the section below simply doesn't render.
  const parentModes = getPentatonicParentModes(rootNote, currentMode);
  const featuredContent = getFeaturedArtistForScale(currentMode);
  const allBackingTracks = getAllBackingTracksSorted(currentMode, rootNote);
  // Lecons Guitar Pro : deja triees par pertinence (gamme courante d'abord).
  const lessons = getLessonsForScale(currentMode, rootNote);
  const { isPurchased, purchasePack, purchasedPacks } = usePackPurchases();
  // useEntitlement re-rende la page quand l'acces global change (achat, Patreon,
  // beta) ; isPackUnlocked / isTrackUnlocked lisent le miroir au moment de l'usage.
  const { unlockFullApp, priceLabel, realPayments } = useEntitlement();
  // Sert la garde "compte avant paiement" et la proposition qui suit l'achat,
  // toutes deux portees par PackPurchaseDialog.
  const { signedIn } = useSignedIn();
  const allPacks = getAllPacks();

  const hasRealVideo = featuredContent?.video?.videoUrl;
  // L'achat porte sur le pack : le contenu video d'un artiste est deverrouille
  // par le (premier) pack de cet artiste.
  const packOfArtist = (artistId) => allPacks.find((p) => p.artistId === artistId) || null;
  // Pack bundled (upScale, Fretboard Fighters) : deverrouille par l'achat de l'app.
  // Pack IAP : deverrouille par son propre achat, pas par full_app.
  const packIsUnlocked = (pack) => isPackUnlocked(pack, !!pack && isPurchased(pack.id));
  const featuredPack = featuredContent ? packOfArtist(featuredContent.artist.id) : null;

  // Split backing tracks by relevance
  const perfectTracks = allBackingTracks.filter(t => t.relevance === 'perfect');
  const compatibleTracks = allBackingTracks.filter(t => t.relevance === 'compatible');
  const sameScaleTracks = allBackingTracks.filter(t => t.relevance === 'sameScale');
  const otherTracks = allBackingTracks.filter(t => t.relevance === 'other');

  // La carte d'info gamme est repliee par defaut : le heros de la page, ce sont
  // les backings jouables, pas la lecon.
  const [moodExpanded, setMoodExpanded] = useState(false);

  // Pack ouvert en page de detail (null = liste Jam normale). Le retour rend la main
  // a la liste sans perdre l'etat de la page.
  const [openPack, setOpenPack] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const pageRef = useRef(null);
  // Deux colonnes au meme moment que You : meme mesure, meme seuil.
  const { split } = useSplitLayout(pageRef, active);
  const positions = useRef({});
  const viewKey = openPack ? `pack-${openPack.id}` : catalog || 'home';

  const navigate = useCallback((nextCatalog, nextPack = null) => {
    const page = pageRef.current;
    positions.current[viewKey] = {
      top: page.scrollTop,
      rails: Object.fromEntries(Array.from(page.querySelectorAll('[data-jam-rail]'), rail => [rail.dataset.jamRail, rail.scrollLeft])),
      focus: document.activeElement?.closest('[data-jam-focus]')?.dataset.jamFocus
    };
    document.dispatchEvent(new Event('backingtrack:stopall'));
    setCatalog(nextCatalog);
    setOpenPack(nextPack);
  }, [viewKey]);

  // Retour a la page d'accueil de Practice depuis l'exterieur : l'onglet
  // Practice quitte une lecon et doit rendre la page principale, pas le detail
  // du pack d'ou la lecon a ete ouverte.
  const goHome = useCallback(() => navigate(null), [navigate]);
  useImperativeHandle(ref, () => ({ goHome }), [goHome]);

  useEffect(() => {
    if (!openPackId) return;
    const pack = getPackById(openPackId);
    if (pack) {
      setCatalog(null);
      setOpenPack(pack);
    }
    if (onOpenPackIdConsumed) onOpenPackIdConsumed();
  }, [openPackId, onOpenPackIdConsumed]);

  useLayoutEffect(() => {
    const page = pageRef.current;
    const saved = positions.current[viewKey];
    page.scrollTop = saved?.top || 0;
    page.querySelectorAll('[data-jam-rail]').forEach(rail => {
      rail.scrollLeft = saved?.rails?.[rail.dataset.jamRail] || 0;
      rail.dispatchEvent(new Event('scroll'));
    });
    if (saved?.focus) {
      Array.from(page.querySelectorAll('[data-jam-focus]'))
        .find(element => element.dataset.jamFocus === saved.focus)?.focus({ preventScroll: true });
    } else if (viewKey !== 'home') {
      page.querySelector('[data-jam-heading]')?.focus({ preventScroll: true });
    }
  }, [viewKey]);

  // "Show more" state for other tracks
  const [showAllOther, setShowAllOther] = useState(false);
  const INITIAL_OTHER_COUNT = 5;
  const visibleOtherTracks = showAllOther ? otherTracks : otherTracks.slice(0, INITIAL_OTHER_COUNT);

  // Purchase confirmation dialog (pack-level)
  const [purchaseDialogPack, setPurchaseDialogPack] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  const handlePurchase = (pack) => {
    if (pack) setPurchaseDialogPack(pack);
  };

  // Apres un achat, on ouvre le pack qu'on vient de payer plutot que de rendre la
  // main a la liste : c'est ce que l'utilisateur cherchait en achetant, et un
  // backing verrouille amene ici via son propre pack.
  const landOnPack = (pack) => {
    setPurchaseDialogPack(null);
    if (pack) navigate(catalog, pack);
  };

  // Nombre de backings visibles rattaches a un pack (pour le dialog d'achat).
  const packTrackCount = (pack) => {
    const artist = FEATURED_ARTISTS[pack.artistId];
    if (!artist?.backingTracksByScale) return 0;
    return Object.values(artist.backingTracksByScale).flat()
      .filter((t) => t.packId === pack.id && isContentVisible(t)).length;
  };

  return (
    <PageShell
      ref={pageRef}
      label="Practice"
      container="jam"
      desktopBrowser={desktopBrowser}
      className="learn-page"
    >
      {/* Page de detail d'un pack : contenu complet + retour a la liste */}
      {openPack && (() => {
        const packArtist = FEATURED_ARTISTS[openPack.artistId];
        const packTracks = getTracksForPack(openPack.id);
        const packLessons = getAllLessons().filter(({ lesson }) => lesson.packId === openPack.id);
        const promoActive = isPackPromoActive(openPack);
        const packUnlocked = isPackUnlocked(openPack, isPurchased(openPack.id));
        return (
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {/* Retour : colle en haut de la page, le pack se scrolle dessous */}
            <Box sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              pt: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.82)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)'
            }}>
              <Box
                component="button"
                onClick={() => navigate(catalog)}
                sx={{
                  border: 0, background: 'none', font: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  py: 1.5,
                  pr: 2,
                  cursor: 'pointer',
                  color: 'rgba(255,255,255,0.85)',
                  '&:hover': { color: 'white' }
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 24 }} />
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Back</Typography>
              </Box>
            </Box>

            {/* Banniere */}
            <Box sx={{
              position: 'relative',
              width: '100%',
              paddingTop: '42%',
              borderRadius: '16px',
              overflow: 'hidden',
              mb: 2,
              background: openPack.imageUrl
                ? undefined
                : `linear-gradient(135deg, ${ACCENT_COLOR}55 0%, rgba(30,30,40,0.9) 70%)`,
              ...(openPack.imageUrl && {
                backgroundImage: `url(${openPack.imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              })
            }}>
              <Box sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'rgba(0,0,0,0.65)',
                px: 1.5,
                py: 0.5,
                borderRadius: '999px'
              }}>
                {openPack.bundled ? (
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'white' }}>
                    {packUnlocked ? 'Included' : 'Included with app'}
                  </Typography>
                ) : (
                  <>
                    {promoActive && (
                      <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through' }}>
                        {openPack.price}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: promoActive ? '#10b981' : 'white' }}>
                      {promoActive ? openPack.promoPrice : openPack.price}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Titre + artiste */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
              {packArtist && (
                <Box sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: `linear-gradient(135deg, ${ACCENT_COLOR} 0%, ${ACCENT_COLOR}99 100%)`,
                  ...(packArtist.avatar && {
                    backgroundImage: `url(${packArtist.avatar})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  })
                }} />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography component="h1" tabIndex={-1} data-jam-heading sx={{ fontSize: '1.3rem', fontWeight: 'bold', color: 'white', lineHeight: 1.2 }}>
                  {openPack.name}
                </Typography>
                {packArtist && (
                  <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)' }}>
                    {packArtist.name}
                  </Typography>
                )}
              </Box>
            </Box>

            {openPack.description && (
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, mb: 2 }}>
                {openPack.description}
              </Typography>
            )}

            {/* Unlock / Watch */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {packUnlocked ? (
                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '999px',
                  backgroundColor: 'rgba(16,185,129,0.15)'
                }}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#10b981' }}>
                    Unlocked
                  </Typography>
                </Box>
              ) : (
                <Box
                  onClick={() => handlePurchase(openPack)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 2.5,
                    py: 1,
                    borderRadius: '999px',
                    cursor: 'pointer',
                    backgroundColor: 'var(--button)',
                    transition: 'all 0.2s ease',
                    '&:hover': { backgroundColor: 'var(--button-hover)' }
                  }}
                >
                  <LockOpenIcon sx={{ fontSize: 18, color: 'var(--button-text)' }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--button-text)' }}>
                    {openPack.bundled
                      ? `Unlock the app · ${priceLabel}`
                      : `Unlock ${promoActive ? openPack.promoPrice : openPack.price}`}
                  </Typography>
                </Box>
              )}
              {openPack.videoUrl && (
                <Box
                  onClick={() => openVideo(openPack.videoUrl, setVideoUrl)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '999px',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    transition: 'all 0.2s ease',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' }
                  }}
                >
                  <PlayCircleOutlineIcon sx={{ fontSize: 18, color: 'white' }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'white' }}>
                    Watch
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Contenu complet du pack */}
            {packTracks.length > 0 && (
              <>
                <SectionHeader title={`Backing Tracks (${packTracks.length})`} />
                {packTracks.map(({ artist: trackArtist, track }) => (
                  <BackingTrackRow
                    key={track.id}
                    track={track}
                    artist={trackArtist}
                    onJam={() => onEnterJamMode && onEnterJamMode(track, trackArtist)}
                    unlocked={isTrackUnlocked(track, purchasedPacks)}
                    onUnlock={() => handlePurchase(openPack)}
                  />
                ))}
              </>
            )}

            {packLessons.length > 0 && (
              <>
                <SectionHeader title={`Lessons (${packLessons.length})`} />
                <Box sx={catalogGridSx}>
                  {packLessons.map(({ lesson, artist: lessonArtist }) => (
                    <LessonCard
                      key={lesson.id}
                      lesson={lesson}
                      artist={lessonArtist}
                      unlocked={isTrackUnlocked(lesson, purchasedPacks)}
                      onEnterLesson={onEnterLesson}
                      onUnlock={() => handlePurchase(openPack)}
                    />
                  ))}
                </Box>
              </>
            )}

            <Box sx={{ height: '20px' }} />
          </Box>
        );
      })()}

      {!openPack && catalog && (
        <Box sx={{ pt: 1 }}>
          <Box sx={{ position: 'sticky', top: 0, zIndex: 20, py: 1, backgroundColor: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(12px)' }}>
            <Button startIcon={<ChevronLeftIcon />} onClick={() => navigate(null)} sx={{ color: 'white', textTransform: 'none', minHeight: 44 }}>
              Back to Jam
            </Button>
            <PageTitle tabIndex={-1} data-jam-heading sx={{ my: 1 }}>
              {catalog === 'lessons' ? `Lessons (${lessons.length})` : `Artist Packs (${allPacks.length})`}
            </PageTitle>
          </Box>
          <Box sx={catalogGridSx}>
            {catalog === 'lessons' ? lessons.map(({ lesson, artist }) => (
              <LessonCard key={lesson.id} lesson={lesson} artist={artist}
                unlocked={isTrackUnlocked(lesson, purchasedPacks)} onEnterLesson={onEnterLesson}
                onUnlock={() => handlePurchase(getPackById(lesson.packId))} />
            )) : allPacks.map(pack => (
              <PackCard key={pack.id} pack={pack} compact isPurchased={packIsUnlocked(pack)}
                onUnlock={() => handlePurchase(pack)} onOpen={() => navigate(catalog, pack)}
                onWatch={() => openVideo(pack.videoUrl, setVideoUrl)} />
            ))}
          </Box>
          {(catalog === 'lessons' ? lessons : allPacks).length === 0 && (
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', py: 3 }}>No {catalog === 'lessons' ? 'lessons' : 'artist packs'} available yet</Typography>
          )}
        </Box>
      )}

      {!openPack && !catalog && (
      <>
      {/* Header Pills */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        ...(split && { pb: 2, '& .header-pills': { justifyContent: 'flex-start', width: 'auto' } })
      }}>
      {split && <PageTitle>Practice</PageTitle>}
      <HeaderPills
        rootNote={rootNote}
        currentMode={currentMode}
        currentMood={currentMood}
        onScalePillClick={onScalePillClick}
      />

      </Box>

      {/* Content */}
      <Box sx={{ pt: 1 }}>
        <Box className="jam-layout" sx={{
          display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)',
          '& > .jam-primary, & > .jam-secondary': { display: 'contents' },
          ...(split && {
            gridTemplateColumns: 'minmax(0, 2fr) minmax(320px, 1fr)', columnGap: 3,
            alignItems: 'start',
            '& > .jam-primary, & > .jam-secondary': { display: 'block', minWidth: 0 },
            '& .jam-tracks > .jam-section-header:first-child': { mt: 0 }
          })
        }}>
        <Box className="jam-primary">
        <Box className="jam-tracks" sx={{ minWidth: 0, order: 1, containerType: 'inline-size', containerName: 'jam-tracks' }}>
        {/* Pro Artist Section - only when real video exists */}
        {hasRealVideo && (
          <>
            <SectionHeader title="Improvisations" badge="Inspiration" />
            <ProArtistCard
              artist={featuredContent.artist}
              video={featuredContent.video}
              rootNote={rootNote}
              currentMode={currentMode}
              isUnlocked={featuredPack ? packIsUnlocked(featuredPack) : true}
              onPurchase={() => handlePurchase(featuredPack)}
            />
          </>
        )}

        {/* Backing Tracks - Prioritized by relevance. Les backings jouables sont le
            heros de la page ; les packs (acquisition) vivent plus bas. */}
        <SectionHeader title={CONTENT_FLAGS.splitTracksByRelevance ? `Backing Tracks in ${scaleLabel}` : 'Backing Tracks'} />

        {/* Flat list (default when relevance sections are disabled) */}
        {!CONTENT_FLAGS.splitTracksByRelevance && allBackingTracks.map(({ artist, track, relevance }) => (
          <BackingTrackCard
            key={track.id}
            track={track}
            artist={artist}
            badge={`${getDisplayRootNote(track.rootNote, track.mode)} ${track.mode}`}
            badgeColor={RELEVANCE_COLORS[relevance]}
            currentMode={currentMode}
            currentRoot={rootNote}
            onScaleChange={onScaleChange}
            onJam={(scale) => onEnterJamMode && onEnterJamMode(track, artist, scale)}
            onPreviewStart={onStopOtherAudio}
            unlocked={isTrackUnlocked(track, purchasedPacks)}
            onUnlock={() => handlePurchase(getPackById(track.packId))}
          />
        ))}

        {CONTENT_FLAGS.splitTracksByRelevance && (
        <>
        {/* Perfect match tracks (titled by the "Backing Tracks in <scale>" umbrella + card badge) */}
        {perfectTracks.map(({ artist, track }) => (
          <BackingTrackCard
            key={track.id}
            track={track}
            artist={artist}
            badge={RELEVANCE_LABELS.perfect}
            badgeColor={RELEVANCE_COLORS.perfect}
            currentMode={currentMode}
            currentRoot={rootNote}
            onScaleChange={onScaleChange}
            onJam={(scale) => onEnterJamMode && onEnterJamMode(track, artist, scale)}
            onPreviewStart={onStopOtherAudio}
            unlocked={isTrackUnlocked(track, purchasedPacks)}
            onUnlock={() => handlePurchase(getPackById(track.packId))}
          />
        ))}

        {/* Compatible tracks */}
        {compatibleTracks.length > 0 && (
          <>
            <SectionHeader title="Compatible Backing Tracks" />
            {compatibleTracks.map(({ artist, track }) => (
              <BackingTrackCard
                key={track.id}
                track={track}
                artist={artist}
                badge={RELEVANCE_LABELS.compatible}
                badgeColor={RELEVANCE_COLORS.compatible}
                currentMode={currentMode}
                currentRoot={rootNote}
                onScaleChange={onScaleChange}
                onJam={(scale) => onEnterJamMode && onEnterJamMode(track, artist, scale)}
                onPreviewStart={onStopOtherAudio}
                unlocked={isTrackUnlocked(track, purchasedPacks)}
                onUnlock={() => handlePurchase(getPackById(track.packId))}
              />
            ))}
          </>
        )}

        {/* Same scale, other key — full cards; same mode in a different root, tapping transposes */}
        {sameScaleTracks.length > 0 && (
          <>
            <SectionHeader title={`${modeLabel} Backing Tracks, Other Keys`} />
            {sameScaleTracks.map(({ artist, track }) => (
              <BackingTrackCard
                key={track.id}
                track={track}
                artist={artist}
                badge={RELEVANCE_LABELS.sameScale}
                badgeColor={RELEVANCE_COLORS.sameScale}
                currentMode={currentMode}
                currentRoot={rootNote}
                onScaleChange={onScaleChange}
                onJam={(scale) => onEnterJamMode && onEnterJamMode(track, artist, scale)}
                onPreviewStart={onStopOtherAudio}
                unlocked={isTrackUnlocked(track, purchasedPacks)}
                onUnlock={() => handlePurchase(getPackById(track.packId))}
              />
            ))}
          </>
        )}

        </>
        )}
        {allBackingTracks.length === 0 && (
          <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 3 }}>
            No backing tracks available yet
          </Typography>
        )}
        </Box>
        {lessons.length > 0 && (
          <ContentRail id="jam-lessons" title="Lessons" count={lessons.length} onViewAll={() => navigate('lessons')}>
            {lessons.map(({ lesson, artist }) => (
              <LessonCard key={lesson.id} lesson={lesson} artist={artist}
                unlocked={isTrackUnlocked(lesson, purchasedPacks)} onEnterLesson={onEnterLesson}
                onUnlock={() => handlePurchase(getPackById(lesson.packId))} />
            ))}
          </ContentRail>
        )}

        {allPacks.length > 0 && (
          <ContentRail id="jam-packs" title="Artist Packs" count={allPacks.length} onViewAll={() => navigate('packs')}>
            {allPacks.map(pack => (
              <Box key={pack.id} sx={{ width: 300, maxWidth: '100%', flexShrink: 0 }}>
                <PackCard pack={pack} compact isPurchased={packIsUnlocked(pack)}
                  onUnlock={() => handlePurchase(pack)} onOpen={() => navigate(null, pack)}
                  onWatch={() => openVideo(pack.videoUrl, setVideoUrl)} />
              </Box>
            ))}
          </ContentRail>
        )}

        </Box>
        <Box className="jam-secondary">
        <Box className="jam-scale" sx={{ minWidth: 0, order: 0 }}>
        {/* Scale Mood Banner - repliee en une ligne, la lecon se deplie a la demande */}
        <Box
          onClick={() => setMoodExpanded(!moodExpanded)}
          sx={{
            backgroundColor: 'rgba(30, 30, 40, 0.6)',
            borderRadius: '16px',
            p: 2,
            mb: 2,
            cursor: 'pointer'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <SchoolIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
                {`What gives ${currentMode} its distinctive sound`}
              </Typography>
            </Box>
            <ExpandMoreIcon sx={{
              color: 'rgba(255,255,255,0.5)',
              flexShrink: 0,
              transition: 'transform 0.2s ease',
              transform: moodExpanded ? 'rotate(180deg)' : 'none'
            }} />
          </Box>
          {moodExpanded && (
            <>
              <Typography sx={{
                fontSize: '0.95rem',
                color: 'rgba(255,255,255,0.8)',
                mt: 1.5,
                mb: 2,
                lineHeight: 1.5
              }}>
                {currentMode} is {modeInfo.description.charAt(0).toLowerCase() + modeInfo.description.slice(1)}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(modeInfo.tags || []).map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: '0.8rem',
                      height: 28
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>

        {/* Parent Scales - only for pentatonics: the scales this pentatonic fits
            inside, at the same root. Tap one to switch and explore it. */}
        {parentModes.length > 0 && (
          <Box sx={{
            backgroundColor: 'rgba(16, 185, 129, 0.12)',
            borderRadius: '16px',
            p: 2.5,
            mb: 2
          }}>
            <Typography sx={{
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: 'rgba(255,255,255,0.6)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              mb: 0.5
            }}>
              Fits Inside
            </Typography>
            <Typography sx={{
              fontSize: '0.95rem',
              color: 'rgba(255,255,255,0.8)',
              mb: 1.5,
              lineHeight: 1.5
            }}>
              Play {getDisplayRootNote(rootNote, currentMode)} {modeInfo.displayName || currentMode} over any of these scales:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {parentModes.map((p) => (
                <Chip
                  key={p.mode}
                  label={`${getDisplayRootNote(p.root, p.mode)} ${p.displayName}`}
                  onClick={onScaleChange ? () => onScaleChange(p.root, p.mode) : undefined}
                  clickable={!!onScaleChange}
                  sx={{
                    backgroundColor: 'rgba(16, 185, 129, 0.25)',
                    color: 'white',
                    fontSize: '0.85rem',
                    height: 32,
                    '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.4)' }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        </Box>
        {CONTENT_FLAGS.splitTracksByRelevance && (
        <Box className="jam-explore" sx={{ minWidth: 0, order: 2 }}>
        {/* When nothing matches above, show hint + other tracks inline */}
        {perfectTracks.length === 0 && compatibleTracks.length === 0 && sameScaleTracks.length === 0 && visibleOtherTracks.length > 0 && (
          <Typography sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, mb: 2 }}>
            No backing tracks for {currentMode} yet, explore these:
          </Typography>
        )}

        {/* Other tracks — compact discovery rows, separate section only when matches above */}
        {visibleOtherTracks.length > 0 && (perfectTracks.length > 0 || compatibleTracks.length > 0 || sameScaleTracks.length > 0) && (
          <SectionHeader title="Explore Other Scales" />
        )}
        {visibleOtherTracks.map(({ artist, track }) => (
          <BackingTrackRow
            key={track.id}
            track={track}
            artist={artist}
            onJam={() => onEnterJamMode && onEnterJamMode(track, artist)}
            unlocked={isTrackUnlocked(track, purchasedPacks)}
            onUnlock={() => handlePurchase(getPackById(track.packId))}
          />
        ))}
        {!showAllOther && otherTracks.length > INITIAL_OTHER_COUNT && (
          <Button
            fullWidth
            onClick={() => setShowAllOther(true)}
            sx={{
              py: 1.2,
              borderRadius: '12px',
              color: 'rgba(255,255,255,0.6)',
              backgroundColor: 'rgba(255,255,255,0.06)',
              textTransform: 'none',
              fontSize: '0.9rem',
              mt: 1,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Show {otherTracks.length - INITIAL_OTHER_COUNT} more
          </Button>
        )}
        </Box>
        )}
        </Box>
        </Box>

        {/* More Inspiration - horizontal scroll of all real videos */}
        {getAllVideos().length > 0 && (
          <>
            <SectionHeader title="More Inspiration" />
            <Box sx={{
              display: 'flex',
              gap: 1.5,
              overflowX: 'auto',
              pb: 2,
              mx: -2,
              px: 2,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' }
            }}>
              {getAllVideos().map(({ artist, video, scaleName }) => (
                <MiniVideoCard
                  key={video.id}
                  title={video.title}
                  duration={video.duration}
                  channel={artist.name}
                  thumbnailUrl={video.thumbnailUrl}
                  onClick={() => {
                    onScaleChange(rootNote, scaleName);
                    document.querySelector('.learn-page')?.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              ))}
            </Box>
          </>
        )}

        {/* More Backing Tracks - horizontal scroll of all real tracks */}
        {CONTENT_FLAGS.showMoreBackingTracksCarousel && getAllBackingTracks().length > 0 && (
          <>
            <SectionHeader title="More Backing Tracks" />
            <Box sx={{
              display: 'flex',
              gap: 1.5,
              overflowX: 'auto',
              pb: 2,
              mx: -2,
              px: 2,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' }
            }}>
              {getAllBackingTracks().map(({ artist, track, scaleName }) => (
                <MiniVideoCard
                  key={track.id}
                  title={track.title}
                  duration={track.duration}
                  channel={artist.name}
                  onClick={() => onEnterJamMode && onEnterJamMode(track, artist)}
                />
              ))}
            </Box>
          </>
        )}

        <Box sx={{ height: '20px' }} />
      </Box>
      </>
      )}

      {/* Le dialogue porte lui-meme ses deux gestes d'achat depuis S9 : ils
          doivent passer par le compte avant la caisse sur le web (F3) et
          proposer le compte apres (famille F1), comme partout ailleurs. */}
      <PackPurchaseDialog
        pack={purchaseDialogPack}
        signedIn={signedIn}
        priceLabel={priceLabel}
        realPayments={realPayments}
        trackCount={purchaseDialogPack ? packTrackCount(purchaseDialogPack) : 0}
        purchasePack={purchasePack}
        unlockFullApp={unlockFullApp}
        onClose={() => setPurchaseDialogPack(null)}
        onPurchased={landOnPack}
        onRequestSignUp={onRequestSignUp}
      />
      <VideoDialog url={videoUrl} onClose={() => setVideoUrl(null)} />
    </PageShell>
  );
});

export default Learn;
